import { ArrowRight, ArrowUpRight, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import ShimmerDots from "../components/ShimmerDots";

// Import hover images
import sxmHover from "../assets/home-images/sxm.png";
import sasHover from "../assets/home-images/sas.png";
import blossomHover from "../assets/home-images/blossom.png";
import aboutHover from "../assets/home-images/about.png";
import sxHover from "../assets/home-images/sx.png";
import arcadeHover from "../assets/arcade/arcade-video.jpg";
// import galleryHover from "../assets/home-images/gallery.png";
// import photographyHover from "../assets/home-images/photography.jpg";

// Reusable ProjectButton component
const ProjectButton = ({
	title,
	description,
	period,
	linkType = "default",
	link,
	onProtectedClick,
	hoverImage,
	onHover,
	onLeave,
}) => {
	const navigate = useNavigate();
	const buttonRef = useRef(null);

	const handleMouseEnter = () => {
		if (buttonRef.current && onHover && hoverImage) {
			const rect = buttonRef.current.getBoundingClientRect();
			const position = {
				x: rect.left - 16, // 16px offset to the left of the button
				y: rect.top + rect.height / 2,
				elementWidth: rect.width,
				elementHeight: rect.height,
			};
			onHover(position, hoverImage);
		}
	};

	const handleClick = (e) => {
		if (!link) return;

		if (linkType === "external") {
			// Open external links in new tab
			window.open(link, "_blank", "noopener,noreferrer");
		} else if (linkType === "locked") {
			// Handle protected content
			onProtectedClick && onProtectedClick(e, link);
		} else {
			// Navigate to internal routes
			navigate(link);
		}
	};

	return (
		<div
			ref={buttonRef}
			className="bg-[#fde2d3] p-4 sm:p-6 rounded-2xl transition-all duration-200 cursor-pointer border-[0.5px] border-[#B7B3B0] shadow-[0_4px_10px_rgba(0,0,0,0.15),inset_0_-4px_0_rgba(0,0,0,0.25)] hover:bg-[#F5F1E5] group select-none"
			onClick={handleClick}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={onLeave}
		>
			<div className="flex flex-col gap-2">
				<div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 sm:gap-0">
					<h3 className="text-sm font-medium text-gray-900 mb-1 flex items-center flex-wrap">
						<span className="flex items-center">
							{title}{" "}
							{linkType === "locked" ? (
								<Lock className="inline w-4 h-4 ml-2 transition-transform duration-200 group-hover:translate-y-[-2px]" />
							) : linkType === "external" ? (
								<ArrowUpRight className="inline w-4 h-4 ml-1 transition-transform duration-200 group-hover:translate-x-1 group-hover:translate-y-[-2px]" />
							) : (
								<ArrowRight className="inline w-4 h-4 ml-1 transition-transform duration-200 group-hover:translate-x-1" />
							)}
						</span>
					</h3>
					<span
						className="text-sm self-start sm:self-auto flex-shrink-0"
						style={{ color: "#000000" }}
					>
						{period}
					</span>
				</div>
				<p className="w-full text-sm font-serif whitespace-pre-line text-pretty text-[#6c6a67] break-words">
					{description}
				</p>
			</div>
		</div>
	);
};

// Data arrays for different sections
const projectsData = [
	// {
	// 	title: "Proactive AI",
	// 	description:
	// 		"Exploring the next interface for agents.\nPrototyping how agents can be more context-aware and embedded.",
	// 	period: "2025 — Present",
	// 	linkType: "default",
	// 	link: "/explorations",
	// 	hoverImage: blossomHover, // placeholder
	// },
	{
		title: "Blossom",
		description:
			"Built one of the first-ever AI-native web browsers.\nPioneered features like query auto-routing and multi-tab context.",
		period: "2023 — 2025",
		linkType: "default",
		link: "/blossom",
		hoverImage: blossomHover,
	},
];

const workData = [
	{
		title: "Arcade",
		description:
			"Building visual tools for marketers.\nWorking across product engineering, agents, and prototyping.",
		period: "Jan 2026 — Present",
		linkType: "default",
		link: "/arcade",
		hoverImage: arcadeHover,
	},
	{
		title: "SiriusXM",
		description:
			"Led prototyping efforts for the next-gen SiriusXM in-vehicle app.\nBuilt out an end-to-end voice agent; conceptualized future interfaces.",
		period: "2025",
		linkType: "locked",
		link: "/siriusxm",
		hoverImage: sxmHover,
	},
	{
		title: "SAS",
		description:
			"Created new components for the design system.\nExperimented with RAG + Figma. Started my journey of building with AI.",
		period: "2024",
		linkType: "locked",
		link: "/sas",
		hoverImage: sasHover,
	},
];

const organizationsData = [
	{
		title: "Startup Exchange @ Georgia Tech",
		description:
			"Helped grow the largest student entrepreneurship org in the Southeast.\nLed a team of 10+ across Design, Marketing, and Strategy.",
		period: "2022 — 2025",
		linkType: "external",
		link: "https://www.startup.exchange/",
		hoverImage: sxHover, // Using about image as placeholder for GT org
	},
];

const Home = () => {
	const navigate = useNavigate();
	const [showPasswordPopup, setShowPasswordPopup] = useState(false);
	const [showIncorrectPasswordPopup, setShowIncorrectPasswordPopup] =
		useState(false);
	const [password, setPassword] = useState("");
	const [protectedTarget, setProtectedTarget] = useState(null);
	const [hoveredCompany, setHoveredCompany] = useState(null);
	const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
	const [buttonHoverImage, setButtonHoverImage] = useState(null);
	const [hoverImageDimensions, setHoverImageDimensions] = useState({
		width: 256,
		height: 192,
	});
	const [imageLoaded, setImageLoaded] = useState(false);
	const [preloadComplete, setPreloadComplete] = useState(false);
	const [showTopBorder, setShowTopBorder] = useState(true);
	const [showBottomBorder, setShowBottomBorder] = useState(false);

	useEffect(() => {
		window.scrollTo(0, 0);

		// Preload hover images to eliminate loading delay on first hover
		const imagesToPreload = [
			arcadeHover,
			sxmHover,
			sasHover,
			blossomHover,
			aboutHover,
			sxHover,
		];

		let loadedCount = 0;
		const totalImages = imagesToPreload.length;

		imagesToPreload.forEach((imageSrc) => {
			const img = new Image();
			img.onload = () => {
				loadedCount++;
				if (loadedCount === totalImages) {
					setPreloadComplete(true);
				}
			};
			img.onerror = () => {
				console.warn(`Failed to preload image: ${imageSrc}`);
				loadedCount++;
				if (loadedCount === totalImages) {
					setPreloadComplete(true);
				}
			};
			img.src = imageSrc;
		});
	}, []);

	useEffect(() => {
		const handleScroll = () => {
			setShowTopBorder(window.scrollY <= 1);

			const atBottom =
				window.innerHeight + window.scrollY >=
				document.documentElement.scrollHeight - 1;
			setShowBottomBorder(atBottom);
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		handleScroll();
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	const handleButtonHover = (position, image) => {
		setHoverPosition(position);
		setButtonHoverImage(image);
		setHoveredCompany(null); // Clear company hover when button is hovered
		setImageLoaded(false); // Reset loaded state for new image
	};

	const handleButtonLeave = () => {
		setButtonHoverImage(null);
		setImageLoaded(false);
	};

	const handleProtectedClick = (e, route) => {
		e.preventDefault();
		const accessGranted = localStorage.getItem("protectedAccess") === "granted";
		if (accessGranted) {
			navigate(route);
			return;
		}
		setProtectedTarget(route);
		setShowIncorrectPasswordPopup(false);
		setShowPasswordPopup(true);
	};

	const handlePasswordSubmit = (e) => {
		e.preventDefault();
		if (
			password.trim() === process.env.REACT_APP_SAS_PASSWORD_SEP24 ||
			password.trim() === process.env.NEXT_PUBLIC_SAS_PASSWORD_SEP24
		) {
			localStorage.setItem("protectedAccess", "granted");
			navigate(protectedTarget || "/sas");
			setShowPasswordPopup(false);
		} else {
			setShowIncorrectPasswordPopup(true);
		}
		setPassword("");
	};

	return (
		<div className="relative bg-[#F5F1E5] min-h-screen">
			<ShimmerDots />
			{/* Orange dot */}
			{/* <div className="absolute top-[5px] left-[5px] w-[20px] h-[20px] bg-[#FF5C00] rounded-full"></div> */}

			{/* Side Borders Container - Fixed to viewport */}
			<div className="fixed inset-0 pointer-events-none z-0">
				{/* Left border */}
				<div
					className="absolute top-0 bottom-0 left-[30px] w-[2px] z-10"
					style={{
						background: `linear-gradient(to right, #BFA99F 0%, #BFA99F 50%, #FFFFFF 50%, #FFFFFF 100%)`,
					}}
				/>

				{/* Right border */}
				<div
					className="absolute top-0 bottom-0 right-[30px] w-[2px] z-10"
					style={{
						background: `linear-gradient(to right, #BFA99F 0%, #BFA99F 50%, #FFFFFF 50%, #FFFFFF 100%)`,
					}}
				/>

				{/* Vertical middle border - hidden on mobile */}
				<div
					className="hidden lg:block absolute top-0 bottom-0 left-1/2 w-[2px] transform -translate-x-1/2 z-10"
					style={{
						background: `linear-gradient(to right, #BFA99F 0%, #BFA99F 50%, #FFFFFF 50%, #FFFFFF 100%)`,
					}}
				/>
			</div>

			{/* Top border - fixed, only visible when scrolled to top */}
			{showTopBorder && (
				<div
					className="fixed left-0 right-0 top-[30px] h-[2px] z-10 pointer-events-none"
					style={{
						background: `linear-gradient(to bottom, #FFFFFF 0%, #FFFFFF 50%, #BFA99F 50%, #BFA99F 100%)`,
					}}
				/>
			)}

			{/* Content Container */}
			<div className="relative z-10 min-h-screen flex flex-col lg:flex-row">
				{/* Left Section */}
				<div className="w-full lg:w-1/2 p-4">
					<div className="flex flex-col justify-between h-full p-8 lg:pl-16 lg:pr-8 lg:py-16">
						{/* Bio Section */}
						<div className="space-y-6 max-w-md font-serif">
							{/* Filler Art */}
							<div className="w-[20px] bg-[#FF5C00] h-[20px] rounded-full"></div>
							<p className="text-xl leading-relaxed font-normal text-gray-900">
								Hi, I'm <span className="italic">Arjun</span>, a{" "}
								<span className="underline underline-offset-2">
									product & design engineer
								</span>{" "}
								based in San Francisco.
							</p>
							<p className="text-xl leading-relaxed font-normal text-gray-900">
								I'm currently an AI Product Engineer at{" "}
								<a
									className="font-semibold inline-block hover:-translate-y-[2px] hover:underline hover:underline-offset-2 transition-all duration-200"
									href="https://www.arcade.software/"
									target="_blank"
									rel="noopener noreferrer"
								>
									Arcade
								</a>
								{", where I'm building a frontier video generation agent. "}
								<button
									className="group inline-flex items-center space-x-[2px] text-xl text-[#918D8B] rounded-full hover:underline hover:bg-[#F5F1E5] transition-all duration-200 select-none"
									onClick={() => navigate("/about")}
								>
									<span>More about me</span>
									<ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
								</button>
							</p>
						</div>

						{/* Hover Image Container */}
						{(hoveredCompany || buttonHoverImage) && (
							<div
								className="fixed z-50 pointer-events-none"
								style={{
									left: buttonHoverImage
										? Math.max(
												20, // minimum 20px from left edge
												Math.min(
													hoverPosition.x - hoverImageDimensions.width - 16, // 16px offset to the left of button
													window.innerWidth - hoverImageDimensions.width - 20, // prevent right edge crop
												),
											)
										: Math.min(
												hoverPosition.x,
												window.innerWidth - hoverImageDimensions.width - 20, // image width + 20px margin
											),
									top: Math.max(
										20, // minimum 20px from top
										Math.min(
											hoverPosition.y - hoverImageDimensions.height / 2, // center vertically using actual image height
											window.innerHeight - hoverImageDimensions.height - 20, // actual image height + margin
										),
									),
								}}
							>
								<div className="relative hover-image-container">
									<img
										src={
											buttonHoverImage ||
											(hoveredCompany === "sxm"
												? sxmHover
												: hoveredCompany === "sas"
													? sasHover
													: hoveredCompany === "blossom"
														? blossomHover
														: hoveredCompany === "gt"
															? aboutHover
															: null)
										}
										alt=""
										className={`w-64 h-auto rounded-xl shadow-2xl hover-image transition-opacity duration-300 ${
											imageLoaded ? "opacity-100" : "opacity-0"
										}`}
										style={{
											// Ensure image takes up space even before loading
											minHeight: preloadComplete ? "auto" : "120px",
										}}
										onLoad={(e) => {
											const { naturalWidth, naturalHeight } = e.target;
											// Calculate scaled dimensions (w-64 = 256px width)
											const scaledWidth = 256;
											const scaledHeight =
												(naturalHeight * scaledWidth) / naturalWidth;
											setHoverImageDimensions({
												width: scaledWidth,
												height: scaledHeight,
											});
											setImageLoaded(true);
										}}
										onError={() => {
											console.warn("Failed to load hover image");
											setImageLoaded(true); // Show anyway to prevent infinite loading
										}}
									/>
									{/* Loading state - only show if preload isn't complete and image isn't loaded */}
									{!imageLoaded && !preloadComplete && (
										<div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-xl">
											<div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
										</div>
									)}
								</div>
							</div>
						)}

						{/* Footer Links */}
						<div className="flex items-center space-x-6 mt-8 font-serif">
							<button
								className="group flex items-center space-x-[2px] text-[#918D8B] rounded-full hover:underline hover:bg-[#F5F1E5] transition-all duration-200 select-none"
								onClick={() => navigate("/photography")}
							>
								<span>Photography</span>
								<ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
							</button>
							<button className="group flex items-center space-x-[2px] text-[#918D8B] rounded-full hover:underline hover:bg-[#F5F1E5] transition-all duration-200 select-none">
								<a
									href="https://x.com/arjundewan_"
									target="_blank"
									rel="noopener noreferrer"
								>
									Twitter
								</a>
								<ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1 group-hover:translate-y-[-2px]" />
							</button>
							<button className="group flex items-center space-x-[2px] text-[#918D8B] rounded-full hover:underline hover:bg-[#F5F1E5] transition-all duration-200 select-none">
								<a
									href="https://www.linkedin.com/in/arjunjdewan/"
									target="_blank"
									rel="noopener noreferrer"
								>
									LinkedIn
								</a>
								<ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1 group-hover:translate-y-[-2px]" />
							</button>
						</div>
					</div>
				</div>

				{/* Right Section */}
				<div className="w-full lg:w-1/2 p-4">
					<div className="flex flex-col h-full p-8 lg:pl-8 lg:pr-16 lg:py-16 space-y-8">
						{/* Work Section */}
						<div className="space-y-4">
							<h2 className="text-sm uppercase tracking-wider font-mono text-[#7E7B78] select-none">
								WORK
							</h2>

							<div className="space-y-4">
								{workData.map((work, index) => (
									<ProjectButton
										key={index}
										title={work.title}
										description={work.description}
										period={work.period}
										linkType={work.linkType}
										link={work.link}
										onProtectedClick={handleProtectedClick}
										hoverImage={work.hoverImage}
										onHover={handleButtonHover}
										onLeave={handleButtonLeave}
									/>
								))}
							</div>
						</div>

						{/* Creations Section */}
						<div className="space-y-4">
							<h2 className="text-sm uppercase tracking-wider font-mono text-[#7E7B78] select-none">
								CREATIONS
							</h2>

							<div className="space-y-4">
								{projectsData.map((project, index) => (
									<ProjectButton
										key={index}
										title={project.title}
										description={project.description}
										period={project.period}
										linkType={project.linkType}
										link={project.link}
										onProtectedClick={handleProtectedClick}
										hoverImage={project.hoverImage}
										onHover={handleButtonHover}
										onLeave={handleButtonLeave}
									/>
								))}
							</div>
						</div>

						{/* Organizations Section */}
						<div className="space-y-4 mb-16">
							<h2 className="text-sm uppercase tracking-wider font-mono text-[#7E7B78] select-none">
								ORGS
							</h2>

							<div className="space-y-4">
								{organizationsData.map((org, index) => (
									<ProjectButton
										key={index}
										title={org.title}
										description={org.description}
										period={org.period}
										linkType={org.linkType}
										link={org.link}
										onProtectedClick={handleProtectedClick}
										hoverImage={org.hoverImage}
										onHover={handleButtonHover}
										onLeave={handleButtonLeave}
									/>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Bottom border - fixed, only visible when scrolled to bottom */}
			{showBottomBorder && (
				<div
					className="fixed left-0 right-0 bottom-[30px] h-[2px] z-10 pointer-events-none"
					style={{
						background: `linear-gradient(to bottom, #FFFFFF 0%, #FFFFFF 50%, #BFA99F 50%, #BFA99F 100%)`,
					}}
				/>
			)}

			{showPasswordPopup && (
				<div className="fixed inset-0 flex items-center justify-center z-50">
					<div className="absolute inset-0 bg-black bg-opacity-50" />
					<div className="flex flex-col bg-white p-6 rounded-lg w-[90%] max-w-[300px] items-center z-10 relative">
						<div className="flex mb-4">
							<p className="text-xl font-semibold text-center">
								This case study is password protected.
							</p>
						</div>
						<form className="w-full" onSubmit={handlePasswordSubmit}>
							<input
								autoFocus
								type="password"
								className="flex rounded w-full px-2 py-1 mb-2 bg-neutral-100"
								placeholder="Password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								autoComplete="off"
							/>
							{showIncorrectPasswordPopup && (
								<div className="flex w-full mb-4 justify-center">
									<p className="text-red-600 bg-red-100 px-3 py-1 rounded-md">
										Incorrect password
									</p>
								</div>
							)}
							<div className="flex justify-end">
								<button
									type="button"
									className="mr-2 px-4 py-2 text-gray-600"
									onClick={() => setShowPasswordPopup(false)}
								>
									Cancel
								</button>
								<button
									type="submit"
									className="px-8 py-2 bg-[#FF5C00] text-white rounded-lg shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_-2px_1px_0_rgba(0,0,0,0.08)_inset,0_2px_1px_0_rgba(255,255,255,0.5)_inset,0_2px_5px_-1px_rgba(0,0,0,0.05),0_1px_3px_-1px_rgba(0,0,0,0.3)] border border-accent/20 active:opacity-80"
								>
									Enter
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</div>
	);
};

export default Home;

// // Stable hover assets and counts so we can memoize and avoid re-renders
// const HOVER_IMAGE_MAP = {
// 	0: ["blossom.png", "blossom-2.png"],
// 	1: ["sas.png"],
// 	2: ["sxm.png"],
// 	4: ["photography.jpg", "photography-2.jpg", "photography-3.jpg"],
// 	5: ["about.png"],
// };

// const HOVER_COUNT_MAP = {
// 	0: 2,
// 	1: 1,
// 	2: 1,
// 	4: 3,
// 	5: 1,
// };

// const createRandomFloatingVariants = (index) => ({
// 	initial: { opacity: 0, scale: 0.95 },
// 	animate: {
// 		opacity: 1,
// 		scale: 1,
// 		x: [0, 8 + index * 2, 0, -8 - index * 2, 0],
// 		y: [0, 8 + index * 3, 0, -8 - index * 3, 0],
// 		rotate: [0, 1, 0, -1, 0],
// 		transition: {
// 			opacity: { duration: 0.3 },
// 			scale: { duration: 0.3 },
// 			x: {
// 				duration: 6 + index,
// 				ease: "easeInOut",
// 				repeat: Infinity,
// 				times: [0, 0.25, 0.5, 0.75, 1],
// 			},
// 			y: {
// 				duration: 6 + index * 1.5,
// 				ease: "easeInOut",
// 				repeat: Infinity,
// 				times: [0, 0.25, 0.5, 0.75, 1],
// 			},
// 			rotate: {
// 				duration: 8 + index * 2,
// 				ease: "easeInOut",
// 				repeat: Infinity,
// 				times: [0, 0.25, 0.5, 0.75, 1],
// 			},
// 		},
// 	},
// 	exit: {
// 		opacity: 0,
// 		scale: 0.95,
// 		transition: { duration: 0.2 },
// 	},
// });

// const Home = () => {
// 	const [isHovered, setIsHovered] = useState(-1);
// 	const [showPasswordPopup, setShowPasswordPopup] = useState(false);
// 	const [showIncorrectPasswordPopup, setShowIncorrectPasswordPopup] =
// 		useState(false);
// 	const [password, setPassword] = useState("");
// 	const [siriusRotation, setSiriusRotation] = useState(-1);
// 	const [georgiaRotation, setGeorgiaRotation] = useState(1);
// 	const [protectedTarget, setProtectedTarget] = useState(null);
// 	const [hasAccess, setHasAccess] = useState(
// 		() =>
// 			typeof window !== "undefined" &&
// 			localStorage.getItem("protectedAccess") === "granted"
// 	);
// 	const navigate = useNavigate();

// 	useEffect(() => {
// 		window.scrollTo(0, 0);
// 	}, []);

// 	useEffect(() => {
// 		const images = [
// 			"blossom.png",
// 			"blossom-2.png",
// 			"sas.png",
// 			"sx.png",
// 			"photography.jpg",
// 			"photography-2.jpg",
// 			"photography-3.jpg",
// 			"about.png",
// 		];

// 		images.forEach((imageName) => {
// 			const img = new Image();
// 			img.src = require(`../assets/home-images/${imageName}`);
// 		});
// 	}, []);

// 	const handleProtectedClick = (e, route) => {
// 		e.preventDefault();
// 		const accessGranted = localStorage.getItem("protectedAccess") === "granted";
// 		if (accessGranted) {
// 			navigate(route, { state: { isAuthenticated: true } });
// 			return;
// 		}
// 		setProtectedTarget(route);
// 		setShowIncorrectPasswordPopup(false);
// 		setShowPasswordPopup(true);
// 	};

// 	const handlePasswordSubmit = (e) => {
// 		e.preventDefault();
// 		if (
// 			password.trim() === process.env.REACT_APP_SAS_PASSWORD_SEP24 ||
// 			password.trim() === process.env.NEXT_PUBLIC_SAS_PASSWORD_SEP24
// 		) {
// 			localStorage.setItem("protectedAccess", "granted");
// 			setHasAccess(true);
// 			navigate(protectedTarget || "/sas", { state: { isAuthenticated: true } });
// 			setShowPasswordPopup(false);
// 		} else {
// 			setShowIncorrectPasswordPopup(true);
// 		}
// 		setPassword("");
// 	};

// 	// LinkButton component for card links with 3D look only on hover + tracking light effect
// 	const LinkButton = ({
// 		to,
// 		onMouseEnter,
// 		onMouseLeave,
// 		onClick,
// 		className = "",
// 		children,
// 	}) => {
// 		const [isBtnHovered, setIsBtnHovered] = useState(false);
// 		const btnRef = useRef(null);
// 		const lightRef = useRef(null);
// 		const rafIdRef = useRef(null);

// 		const handleMouseMove = (e) => {
// 			if (!btnRef.current || !lightRef.current) return;
// 			const rect = btnRef.current.getBoundingClientRect();
// 			const x = e.clientX - rect.left - 80;
// 			const y = e.clientY - rect.top - 80;
// 			if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
// 			rafIdRef.current = requestAnimationFrame(() => {
// 				if (lightRef.current) {
// 					lightRef.current.style.transform = `translate3d(${x}px, ${y}px, 0)`;
// 				}
// 			});
// 		};

// 		return (
// 			<Link
// 				ref={btnRef}
// 				to={to}
// 				className={`relative overflow-hidden card flex p-4 justify-between items-center rounded-xl font-semibold text-black bg-transparent border-none shadow-none text-base transform-gpu will-change-[transform,box-shadow] transition-[box-shadow,background-color,transform] duration-200 ease-out hover:bg-gradient-to-b hover:from-white/0 hover:via-white/90 hover:to-white hover:shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_-2px_1px_0_rgba(0,0,0,0.08)_inset,0_2px_1px_0_rgba(255,255,255,0.5)_inset,0_2px_5px_-1px_rgba(0,0,0,0.05),0_1px_3px_-1px_rgba(0,0,0,0.3)] hover:border hover:border-accent/20 hover:bg-neutral-50 active:bg-neutral-200 ${className}`}
// 				onMouseEnter={(e) => {
// 					setIsBtnHovered(true);
// 					onMouseEnter && onMouseEnter(e);
// 				}}
// 				onMouseLeave={(e) => {
// 					setIsBtnHovered(false);
// 					onMouseLeave && onMouseLeave(e);
// 				}}
// 				onMouseMove={handleMouseMove}
// 				onClick={onClick}
// 				style={{ position: "relative" }}
// 			>
// 				{/* Tracking light effect */}
// 				{isBtnHovered && (
// 					<span
// 						ref={lightRef}
// 						className="pointer-events-none absolute z-20"
// 						style={{
// 							left: 0,
// 							top: 0,
// 							width: 160,
// 							height: 160,
// 							background:
// 								"radial-gradient(circle, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.18) 60%, transparent 100%)",
// 							filter: "blur(8px)",
// 							transition: "transform 60ms linear",
// 							borderRadius: "50%",
// 							mixBlendMode: "lighten",
// 							pointerEvents: "none",
// 							willChange: "transform",
// 							transform: "translate3d(0,0,0)",
// 							backfaceVisibility: "hidden",
// 						}}
// 					/>
// 				)}
// 				{children}
// 			</Link>
// 		);
// 	};

// 	// New component for rotatable span
// 	const RotatableSpan = ({
// 		children,
// 		baseRotation,
// 		className,
// 		onRotationChange,
// 	}) => {
// 		const spanRef = useRef(null);

// 		const handleMouseMove = (e) => {
// 			if (!spanRef.current) return;

// 			const rect = spanRef.current.getBoundingClientRect();
// 			const centerX = rect.left + rect.width / 2;
// 			const centerY = rect.top + rect.height / 2;

// 			// Calculate distance from center
// 			const mouseX = e.clientX - centerX;
// 			const mouseY = e.clientY - centerY;

// 			// Calculate additional rotation based on mouse position
// 			// The further from center, the more rotation (max ±3 degrees)
// 			const maxAdditionalRotation = 3;
// 			const distance = Math.sqrt(mouseX * mouseX + mouseY * mouseY);
// 			const maxDistance = 50; // pixels
// 			const rotationIntensity = Math.min(distance / maxDistance, 1);

// 			// Calculate angle based on mouse position
// 			const angle = Math.atan2(mouseY, mouseX);
// 			const additionalRotation =
// 				Math.sin(angle) * rotationIntensity * maxAdditionalRotation * 5;

// 			onRotationChange(baseRotation + additionalRotation);
// 		};

// 		const handleMouseLeave = () => {
// 			onRotationChange(baseRotation);
// 		};

// 		return (
// 			<span
// 				ref={spanRef}
// 				className={className}
// 				onMouseMove={handleMouseMove}
// 				onMouseLeave={handleMouseLeave}
// 				style={{
// 					transform: `rotate(${
// 						onRotationChange === setSiriusRotation
// 							? siriusRotation
// 							: georgiaRotation
// 					}deg)`,
// 					transition: "transform 0.1s ease-out",
// 				}}
// 			>
// 				{children}
// 			</span>
// 		);
// 	};

// 	return (
// 		<div
// 			className="flex flex-col lg:flex-row p-4 lg:p-20 min-h-screen bg-secondary bg-cover bg-center bg-no-repeat"
// 			style={{
// 				backgroundImage: `linear-gradient(rgba(245,241,228,0.9), rgba(245,241,228,0.9)), url(${bgImage})`,
// 			}}
// 		>
// 			<div className="content flex flex-col w-full lg:min-w-[500px] lg:max-w-[500px] h-full justify-between">
// 				<div className="header flex mb-8 lg:mb-0 pt-4 pl-4">
// 					<p className="text-xl lg:text-2xl font-light leading-8 text-pretty">
// 						<span className="font-garamond text-3xl tracking-tight relative">
// 							Arjun Dewan
// 						</span>{" "}
// 						is a{" "}
// 						<span className="tracking-tight underline">
// 							product & design engineer
// 						</span>{" "}
// 						specializing in prototyping for AI.
// 						<br />
// 						<br />
// 						He previously developed future automotive interfaces at{" "}
// 						<RotatableSpan
// 							baseRotation={-1}
// 							className="inline-block font-medium text-white bg-[#0000EF] rounded-md px-1 border border-white shadow-md align-middle select-none"
// 							onRotationChange={setSiriusRotation}
// 						>
// 							SiriusXM
// 						</RotatableSpan>{" "}
// 						and is currently wrapping up his degree in Computational Media at{" "}
// 						<RotatableSpan
// 							baseRotation={1}
// 							className="inline-block font-medium text-white bg-[#B3A369] rounded-md px-1 py-0.5 border border-white shadow-md align-middle select-none"
// 							onRotationChange={setGeorgiaRotation}
// 						>
// 							Georgia Tech
// 						</RotatableSpan>
// 						.
// 					</p>
// 				</div>
// 				<div className="body flex flex-col divide-y-2 divide-gray">
// 					<div className="work flex flex-col py-10">
// 						<p className="text-xl lg:text-2xl font-semibold mb-4 ml-4">Work</p>
// 						<div className="cards flex flex-col">
// 							{/* SiriusXM (Protected) */}
// 							<LinkButton
// 								to="/siriusxm"
// 								onMouseEnter={() => setIsHovered(2)}
// 								onMouseLeave={() => setIsHovered(-1)}
// 								onClick={(e) => handleProtectedClick(e, "/siriusxm")}
// 							>
// 								{!hasAccess && (
// 									<div className="ml-20 pointer-events-none absolute inset-0 flex items-center justify-center z-10">
// 										<Lock size={32} className="text-neutral-400" />
// 									</div>
// 								)}
// 								<div className="flex flex-col">
// 									<p className="text-lg lg:text-xl font-semibold">SiriusXM</p>
// 									<p className="text-lg lg:text-xl font-light">
// 										Voice Agents for Automotive
// 									</p>
// 								</div>
// 								<div className="details flex flex-col text-right">
// 									<p className="text-lg font-normal lg:text-xl text-neutral-500">
// 										Internship
// 									</p>
// 									<p className="text-lg font-normal lg:text-xl text-neutral-500">
// 										2025
// 									</p>
// 								</div>
// 							</LinkButton>
// 							<LinkButton
// 								to="/blossom"
// 								onMouseEnter={() => setIsHovered(0)}
// 								onMouseLeave={() => setIsHovered(-1)}
// 							>
// 								<div className="flex flex-col w-1/2 relative z-20">
// 									<p className="text-lg lg:text-xl font-semibold">Blossom</p>
// 									<p className="text-lg lg:text-xl font-light leading-5">
// 										AI-Native Web Browser
// 									</p>
// 								</div>
// 								<div className="details flex flex-col text-right w-1/2 justify-between relative z-20">
// 									<p className="text-lg font-normal lg:text-xl text-neutral-500">
// 										Startup
// 									</p>
// 									<p className="text-lg font-normal lg:text-xl text-neutral-500 leading-5">
// 										2023 - 2025
// 									</p>
// 								</div>
// 							</LinkButton>
// 							<LinkButton
// 								to="/sas"
// 								onMouseEnter={() => setIsHovered(1)}
// 								onMouseLeave={() => setIsHovered(-1)}
// 								onClick={(e) => handleProtectedClick(e, "/sas")}
// 							>
// 								{!hasAccess && (
// 									<div className="ml-20 pointer-events-none absolute inset-0 flex items-center justify-center z-10">
// 										<Lock size={32} className="text-neutral-400" />
// 									</div>
// 								)}
// 								<div className="flex flex-col">
// 									<p className="text-lg lg:text-xl font-semibold">SAS</p>
// 									<p className="text-lg lg:text-xl font-light">
// 										GenAI in Design Systems
// 									</p>
// 								</div>
// 								<div className="details flex flex-col text-right">
// 									<p className="text-lg font-normal lg:text-xl text-neutral-500">
// 										Internship
// 									</p>
// 									<p className="text-lg font-normal lg:text-xl text-neutral-500">
// 										2024
// 									</p>
// 								</div>
// 							</LinkButton>
// 						</div>
// 					</div>
// 					<div className="more flex flex-col py-10">
// 						{/* <p className="text-2xl font-semibold mb-4 ml-4">More</p> */}
// 						<div className="cards flex flex-col">
// 							<LinkButton
// 								to="/photography"
// 								onMouseEnter={() => setIsHovered(4)}
// 								onMouseLeave={() => setIsHovered(-1)}
// 							>
// 								<div className="flex flex-col">
// 									<p className="text-lg lg:text-xl font-semibold">
// 										Photography
// 									</p>
// 									{/* <p className="text-xl">Web Browser for Research</p> */}
// 								</div>
// 								<div className="details flex flex-col text-right">
// 									{/* <p className="text-xl text-neutral-600">Concept</p>
// 									<p className="text-xl text-neutral-600">2023</p> */}
// 								</div>
// 							</LinkButton>
// 							<LinkButton
// 								to="/about"
// 								onMouseEnter={() => setIsHovered(5)}
// 								onMouseLeave={() => setIsHovered(-1)}
// 							>
// 								<div className="flex flex-col">
// 									<p className="text-lg lg:text-xl font-semibold">About</p>
// 									{/* <p className="text-xl">Design Direction</p> */}
// 								</div>
// 								<div className="details flex flex-col text-right">
// 									{/* <p className="text-xl text-neutral-600">Student Org</p>
// 									<p className="text-xl text-neutral-600">2024</p> */}
// 								</div>
// 							</LinkButton>
// 						</div>
// 					</div>
// 				</div>
// 			</div>
// 			<FloatingImageContainer isHovered={isHovered} />
// 			{showPasswordPopup && (
// 				<div className="fixed inset-0 flex items-center justify-center z-50">
// 					<div className="absolute inset-0 bg-black bg-opacity-50" />
// 					<div className="flex flex-col bg-white p-6 rounded-lg w-[90%] max-w-[300px] items-center z-10 relative">
// 						<div className="flex mb-4">
// 							<p className="text-xl font-semibold text-center">
// 								This case study is password protected.
// 							</p>
// 						</div>
// 						<form className="w-full" onSubmit={handlePasswordSubmit}>
// 							<input
// 								autoFocus
// 								type="password"
// 								className="flex rounded w-full px-2 py-1 mb-2 bg-neutral-100"
// 								placeholder="Password"
// 								value={password}
// 								onChange={(e) => setPassword(e.target.value)}
// 								autoComplete="off"
// 							/>
// 							{showIncorrectPasswordPopup && (
// 								<div className="flex w-full mb-4 justify-center">
// 									<p className="text-red-600 bg-red-100 px-3 py-1 rounded-md">
// 										Incorrect password
// 									</p>
// 								</div>
// 							)}
// 							<div className="flex justify-end">
// 								<button
// 									type="button"
// 									className="mr-2 px-4 py-2 text-gray-600"
// 									onClick={() => setShowPasswordPopup(false)}
// 								>
// 									Cancel
// 								</button>
// 								<button
// 									type="submit"
// 									className="px-8 py-2 bg-[#FF5C00] text-white rounded-lg shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_-2px_1px_0_rgba(0,0,0,0.08)_inset,0_2px_1px_0_rgba(255,255,255,0.5)_inset,0_2px_5px_-1px_rgba(0,0,0,0.05),0_1px_3px_-1px_rgba(0,0,0,0.3)] border border-accent/20 active:opacity-80"
// 								>
// 									Enter
// 								</button>
// 							</div>
// 						</form>
// 					</div>
// 				</div>
// 			)}
// 		</div>
// 	);
// };

// // Separate component that only depends on isHovered - isolated from password state
// const FloatingImageContainer = memo(({ isHovered }) => {
// 	const hoverImages = useMemo(
// 		() => HOVER_IMAGE_MAP[isHovered] || [],
// 		[isHovered]
// 	);
// 	const hoverCount = useMemo(
// 		() => HOVER_COUNT_MAP[isHovered] || 0,
// 		[isHovered]
// 	);

// 	return (
// 		<div className="image flex-col mt-8 lg:mt-0 lg:ml-20 w-full justify-center">
// 			<div className="flex w-full h-full items-center">
// 				{isHovered >= 0 && (
// 					<FloatingImageSet
// 						images={hoverImages}
// 						hoveredIndex={isHovered}
// 						count={hoverCount}
// 					/>
// 				)}
// 			</div>
// 		</div>
// 	);
// });

// // FloatingImageSet component for animated hover images
// const FloatingImageSet = ({ images, hoveredIndex, count }) => {
// 	// Array of positions for up to 3 images (responsive design)
// 	const positions = [
// 		{
// 			className: "z-10",
// 			style: {
// 				maxWidth: "clamp(180px, 60vw, 80%)",
// 				maxHeight: "clamp(135px, 50vh, 80%)",
// 			},
// 			rounded: "rounded-xl md:rounded-2xl lg:rounded-3xl",
// 		}, // main
// 		{
// 			className: `absolute left-1 top-6 md:left-4 md:top-2 lg:left-2 lg:top-10 z-${
// 				count < 3 ? 30 : 0
// 			}`,
// 			style: {
// 				maxWidth: "clamp(70px, 22vw, 38%)",
// 				maxHeight: "clamp(52px, 18vh, 38%)",
// 			},
// 			rounded: "rounded-lg md:rounded-xl lg:rounded-2xl",
// 		}, // overlap 1
// 		{
// 			className:
// 				"absolute right-1 bottom-4 md:right-4 md:bottom-2 lg:right-2 lg:bottom-6 z-20",
// 			style: {
// 				maxWidth: "clamp(70px, 22vw, 38%)",
// 				maxHeight: "clamp(52px, 18vh, 38%)",
// 			},
// 			rounded: "rounded-lg md:rounded-xl lg:rounded-2xl",
// 		}, // overlap 2
// 	];

// 	// Only recalc variants when hoveredIndex or count changes (not on unrelated state updates)
// 	const variantsList = useMemo(() => {
// 		const list = [];
// 		for (let i = 0; i < count; i += 1) {
// 			list.push(createRandomFloatingVariants(hoveredIndex + i * 10));
// 		}
// 		return list;
// 	}, [hoveredIndex, count]);

// 	return (
// 		<div className="relative w-full h-full flex items-center justify-center">
// 			{[...Array(count)].map((_, i) => (
// 				<motion.div
// 					key={i}
// 					className={`bg-transparent shadow-[0_0_40px_10px_rgba(0,0,0,0.25)] overflow-hidden ${
// 						positions[i]?.className || "absolute"
// 					} ${positions[i]?.rounded || "rounded-2xl"}`}
// 					style={positions[i]?.style || { maxWidth: "38%", maxHeight: "38%" }}
// 					initial="initial"
// 					animate="animate"
// 					exit="exit"
// 					variants={variantsList[i]}
// 				>
// 					<img
// 						src={require(`../assets/home-images/${images[i % images.length]}`)}
// 						alt={`Floating${i}`}
// 						className={`w-full h-full object-cover ${
// 							positions[i]?.rounded || "rounded-2xl"
// 						}`}
// 					/>
// 				</motion.div>
// 			))}
// 		</div>
// 	);
// };

// export default Home;
